<script setup lang="ts">
import { useBasketStore } from '~/stores/basket'

const { authEnabled } = useRuntimeConfig().public
const { user, isAdmin, displayName, signOut } = useAuth()
const basket = useBasketStore()
const route = useRoute()
const menuOpen = ref(false)
const root = ref<HTMLElement | null>(null)

// Close on a click outside the header (menu included) or on Escape
function onPointerDown(e: PointerEvent) {
  if (root.value && !root.value.contains(e.target as Node)) menuOpen.value = false
}
function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') menuOpen.value = false
}
// The logo and header buttons close the menu even when they don't change the route
function onBarClick(e: MouseEvent) {
  const target = (e.target as Element).closest('a, button')
  if (target && !target.hasAttribute('data-menu-toggle')) menuOpen.value = false
}
function listen(on: boolean) {
  if (on) {
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeydown)
  } else {
    document.removeEventListener('pointerdown', onPointerDown)
    document.removeEventListener('keydown', onKeydown)
  }
}
watch(menuOpen, listen)
onBeforeUnmount(() => listen(false))

watch(() => route.fullPath, () => (menuOpen.value = false))

// Keep the cart badge in sync once signed in (always, when login is off)
// Only clear on a real sign-out: `user` is briefly null while the client session loads
watch(
  user,
  (u, prev) => {
    if ((u || !authEnabled) && import.meta.client) {
      basket.refresh().catch(() => setTimeout(() => basket.refresh().catch(() => {}), 1500))
    } else if (!u && prev) basket.reset()
  },
  { immediate: true },
)

// Other tabs: refetch when this tab regains focus, and when another tab changes the basket
function refreshBasket() {
  if (document.visibilityState === 'visible' && (user.value || !authEnabled)) basket.refresh().catch(() => {})
}
onMounted(() => {
  basket.listen()
  window.addEventListener('focus', refreshBasket)
  document.addEventListener('visibilitychange', refreshBasket)
})
onBeforeUnmount(() => {
  window.removeEventListener('focus', refreshBasket)
  document.removeEventListener('visibilitychange', refreshBasket)
})

async function onSignOut() {
  menuOpen.value = false
  await signOut()
}
</script>

<template>
  <header ref="root" class="header">
    <div class="container header__bar" @click="onBarClick">
      <AppLogo light />
      <nav class="header__actions" aria-label="Main">
        <NuxtLink to="/search" class="header__icon" aria-label="Search">
          <AppIcon name="search" />
        </NuxtLink>
        <NuxtLink to="/basket" class="header__icon" :aria-label="`Basket, ${basket.count} items`">
          <AppIcon name="cart" />
          <span v-if="basket.count" class="header__badge">{{ basket.count }}</span>
        </NuxtLink>
        <NuxtLink to="/followed" class="header__icon" aria-label="Followed deals">
          <AppIcon name="heart" />
        </NuxtLink>
        <button
          type="button"
          class="header__icon"
          data-menu-toggle
          :aria-expanded="menuOpen"
          aria-controls="main-menu"
          aria-label="Menu"
          @click="menuOpen = !menuOpen"
        >
          <AppIcon :name="menuOpen ? 'x' : 'menu'" />
        </button>
      </nav>
    </div>

    <Transition name="drawer">
      <div v-if="menuOpen" id="main-menu" class="menu">
        <div class="container">
          <div class="menu__user">
            <span class="menu__avatar"><AppIcon name="user" :size="22" /></span>
            <div>
              <p class="menu__name">{{ user ? displayName : 'Guest' }}</p>
              <p class="menu__sub text-sm">
                <template v-if="user">{{ user.email }}</template>
                <template v-else-if="authEnabled">Not signed in</template>
                <template v-else>Demo mode — login is turned off</template>
              </p>
            </div>
          </div>
          <ul class="menu__list">
            <li><NuxtLink to="/"><AppIcon name="home" :size="20" /> Home</NuxtLink></li>
            <li><NuxtLink to="/search"><AppIcon name="search" :size="20" /> Find a deal</NuxtLink></li>
            <li><NuxtLink to="/orders"><AppIcon name="package" :size="20" /> Order history</NuxtLink></li>
            <li><NuxtLink to="/followed"><AppIcon name="heart" :size="20" /> Followed deals</NuxtLink></li>
            <li v-if="isAdmin"><NuxtLink to="/admin"><AppIcon name="settings" :size="20" /> Admin</NuxtLink></li>
            <li v-if="user">
              <button type="button" @click="onSignOut"><AppIcon name="log-out" :size="20" /> Sign out</button>
            </li>
            <li v-else><NuxtLink to="/login"><AppIcon name="log-in" :size="20" /> Log in / Sign up</NuxtLink></li>
          </ul>
        </div>
      </div>
    </Transition>
  </header>
</template>

<style scoped>
.header {
  position: sticky;
  top: 0;
  z-index: 40;
  background: var(--brand-900);
  color: var(--white);
}

.header__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: var(--header-h);
}

.header__actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.header__icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--white);
}

.header__icon:hover {
  background: rgba(255, 255, 255, 0.1);
}

.header__badge {
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: var(--radius-pill);
  background: var(--logo-red);
  color: var(--white);
  font-size: 11px;
  line-height: 18px;
  font-weight: 600;
  text-align: center;
}

.menu {
  position: absolute;
  inset: var(--header-h) 0 auto 0;
  background: var(--white);
  color: var(--gray-900);
  box-shadow: var(--shadow-lg);
  padding: 8px 0 16px;
}

.menu__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.menu__list a,
.menu__list button {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px 0;
  border: 0;
  border-bottom: 1px solid var(--gray-200);
  background: none;
  font-size: 16px;
  font-weight: 600;
  text-align: left;
}

.menu__user {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid var(--gray-200);
}

.menu__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 44px;
  height: 44px;
  border-radius: var(--radius-pill);
  background: var(--brand-50);
  color: var(--brand-700);
}

.menu__name {
  font-weight: 600;
}

.menu__sub {
  color: var(--gray-600);
  word-break: break-all;
}

.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.15s, transform 0.15s;
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
